import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const MinistryContext = createContext(null);

// Função para gerar variações de cor (mais clara e mais escura)
const generateColorVariations = (hexColor) => {
  if (!hexColor) return { primary: '#0ea5e9', light: '#e0f2fe', dark: '#0284c7' };
  
  // Remove # se existir
  const hex = hexColor.replace('#', '');
  
  // Converte para RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Gera versão clara (mix com branco)
  const lightR = Math.round(r + (255 - r) * 0.85);
  const lightG = Math.round(g + (255 - g) * 0.85);
  const lightB = Math.round(b + (255 - b) * 0.85);
  const light = `rgb(${lightR}, ${lightG}, ${lightB})`;
  
  // Gera versão escura
  const darkR = Math.round(r * 0.8);
  const darkG = Math.round(g * 0.8);
  const darkB = Math.round(b * 0.8);
  const dark = `rgb(${darkR}, ${darkG}, ${darkB})`;
  
  return {
    primary: hexColor,
    light,
    dark
  };
};

export const MinistryProvider = ({ children }) => {
  const [ministries, setMinistries] = useState([]);
  const [currentMinistry, setCurrentMinistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [colors, setColors] = useState({ primary: '#0ea5e9', light: '#e0f2fe', dark: '#0284c7' });

  const fetchMinistries = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/api/ministries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMinistries(response.data);
      
      // Restaurar ministério salvo ou usar o padrão
      const savedMinistryId = localStorage.getItem('currentMinistryId');
      let ministry = null;
      
      if (savedMinistryId) {
        ministry = response.data.find(m => m.id === savedMinistryId);
      }
      
      if (!ministry) {
        // Usar o ministério padrão
        ministry = response.data.find(m => m.is_default) || response.data[0];
      }
      
      if (ministry) {
        setCurrentMinistry(ministry);
        localStorage.setItem('currentMinistryId', ministry.id);
        setColors(generateColorVariations(ministry.color));
      }
    } catch (error) {
      console.error('Error fetching ministries:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch ministries on mount and when storage changes (for login/logout)
  useEffect(() => {
    fetchMinistries();
    
    // Listen for storage changes (token being set after login)
    const handleStorageChange = (e) => {
      if (e.key === 'token' && e.newValue) {
        fetchMinistries();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also poll for token changes (same tab)
    const checkToken = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token && !currentMinistry && !loading) {
        fetchMinistries();
      }
    }, 500);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(checkToken);
    };
  }, [fetchMinistries, currentMinistry, loading]);

  const selectMinistry = useCallback((ministry) => {
    setCurrentMinistry(ministry);
    localStorage.setItem('currentMinistryId', ministry.id);
    setColors(generateColorVariations(ministry.color));
  }, []);

  const refreshMinistries = useCallback(() => {
    fetchMinistries();
  }, [fetchMinistries]);

  // Função auxiliar para obter o nome no singular/plural
  const getEntityName = useCallback((plural = false) => {
    if (!currentMinistry) return plural ? 'Adolescentes' : 'Adolescente';
    return plural ? currentMinistry.name : currentMinistry.singular_name;
  }, [currentMinistry]);

  const value = {
    ministries,
    currentMinistry,
    selectMinistry,
    refreshMinistries,
    getEntityName,
    loading,
    ministryId: currentMinistry?.id || null,
    colors
  };

  return (
    <MinistryContext.Provider value={value}>
      {children}
    </MinistryContext.Provider>
  );
};

export const useMinistry = () => {
  const context = useContext(MinistryContext);
  if (!context) {
    throw new Error('useMinistry must be used within a MinistryProvider');
  }
  return context;
};

export default MinistryContext;
